import React, { useState } from 'react';
import { useOrderStore } from '../../store/orderStore';
import { Button } from '../ui/button';
import { realShopifyAPI } from '../../services/realShopifyAPI';
import { devShopifyAPI } from '../../services/devShopifyAPI';
import { ShoppingCart, Loader2, CreditCard } from 'lucide-react';

export const RightPaymentPanel: React.FC = () => {
    const {
        selectedProduct,
        selectedSize,
        selectedFrameProduct,
        resetOrder
    } = useOrderStore();

    const [isProcessing, setIsProcessing] = useState(false);
    const [useRealStore, setUseRealStore] = useState(false);

    const getPrice = () => {
        const selectedVariant = selectedProduct?.variants?.find(v => v.id.toString() === selectedSize);
        const frameVariant = selectedFrameProduct?.variants?.[0];

        let totalPrice = 0;

        if (selectedVariant) {
            totalPrice += parseFloat(selectedVariant.price);
        }

        if (frameVariant) {
            totalPrice += parseFloat(frameVariant.price);
        }

        return totalPrice;
    };

    const handlePurchase = async () => {
        setIsProcessing(true);

        try {
            // 注文データの準備
            const selectedVariant = selectedProduct?.variants?.find(v => v.id.toString() === selectedSize);
            const frameVariant = selectedFrameProduct?.variants?.[0];

            const lineItems = [];

            // 用紙商品
            if (selectedProduct && selectedVariant) {
                lineItems.push({
                    productId: selectedProduct.id,
                    variantId: selectedVariant.id,
                    quantity: 1,
                    properties: {
                        'variant_title': selectedVariant.title,
                        'product_type': 'paper',
                        'order_type': 'photo_upload_form'
                    }
                });
            }

            // 額装商品
            if (selectedFrameProduct && frameVariant) {
                lineItems.push({
                    productId: selectedFrameProduct.id,
                    variantId: frameVariant.id,
                    quantity: 1,
                    properties: {
                        'variant_title': frameVariant.title,
                        'product_type': 'frame',
                        'order_type': 'photo_upload_form'
                    }
                });
            }

            if (lineItems.length === 0) {
                alert('商品が選択されていません');
                setIsProcessing(false);
                return;
            }

            const orderData = { lineItems };

            let result;

            if (useRealStore) {
                result = await realShopifyAPI.createOrder(orderData);
            } else {
                result = await devShopifyAPI.createOrder(orderData);
            }

            if (result.success || result.order) {
                const orderId = result.order?.id || result.order?.name || 'N/A';
                const message = useRealStore ? '実ストアで注文が完了しました！' : result.message;

                alert(`${message}\n注文ID: ${orderId}`);
                resetOrder();
            } else {
                alert('注文の処理中にエラーが発生しました');
            }
        } catch (error) {
            console.error('Order creation error:', error);
            alert('注文の処理中にエラーが発生しました');
        } finally {
            setIsProcessing(false);
        }
    };

    const selectedVariant = selectedProduct?.variants?.find(v => v.id.toString() === selectedSize);
    const totalPrice = getPrice();

    return (
        <div className="w-full lg:w-80 bg-white border-t lg:border-t-0 lg:border-l border-gray-200 h-auto lg:h-full overflow-y-auto flex flex-col shadow-lg z-10">
            <div className="p-4 border-b border-gray-100 bg-gray-50">
                <h2 className="text-lg font-bold text-gray-800 flex items-center">
                    <ShoppingCart className="w-5 h-5 mr-2 text-blue-600" />
                    注文概要
                </h2>
            </div>

            <div className="p-4 flex-1 overflow-y-auto">
                {/* 選択されたアイテムのリスト */}
                <div className="space-y-4 mb-6">
                    {/* 用紙 */}
                    <div className="bg-white rounded border border-gray-100 p-3 shadow-sm">
                        <div className="text-xs text-gray-500 mb-1">用紙</div>
                        {selectedProduct ? (
                            <div>
                                <div className="font-medium text-sm text-gray-900">{selectedProduct.title}</div>
                                {selectedVariant && (
                                    <div className="flex justify-between mt-1 text-xs">
                                        <span className="text-gray-600">{selectedVariant.title}</span>
                                        <span className="font-semibold">¥{selectedVariant.price}</span>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="text-sm text-gray-400 italic">未選択</div>
                        )}
                    </div>

                    {/* 額装/パネル/キャンバス */}
                    {selectedFrameProduct && (
                        <div className="bg-white rounded border border-gray-100 p-3 shadow-sm">
                            <div className="text-xs text-gray-500 mb-1">オプション</div>
                            <div>
                                <div className="font-medium text-sm text-gray-900">{selectedFrameProduct.title}</div>
                                <div className="flex justify-between mt-1 text-xs">
                                    <span className="text-gray-600">標準仕様</span>
                                    <span className="font-semibold">¥{selectedFrameProduct.variants?.[0]?.price}</span>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* 合計金額 */}
                <div className="border-t border-gray-200 pt-4 mb-6">
                    <div className="flex justify-between items-end">
                        <span className="text-sm font-medium text-gray-600">合計 (税込)</span>
                        <span className="text-2xl font-bold text-blue-600">¥{totalPrice.toLocaleString()}</span>
                    </div>
                </div>

                {/* ストア切り替え (開発用) */}
                <div className="mb-6 p-3 bg-gray-50 rounded text-xs">
                    <div className="font-semibold mb-2 text-gray-700">接続先ストア</div>
                    <div className="flex flex-col space-y-2">
                        <label className="flex items-center cursor-pointer">
                            <input
                                type="radio"
                                checked={!useRealStore}
                                onChange={() => setUseRealStore(false)}
                                className="mr-2"
                            />
                            開発用モック
                        </label>
                        <label className="flex items-center cursor-pointer">
                            <input
                                type="radio"
                                checked={useRealStore}
                                onChange={() => setUseRealStore(true)}
                                className="mr-2"
                            />
                            実ストア
                        </label>
                    </div>
                </div>
            </div>

            {/* 決済ボタン */}
            <div className="p-4 border-t border-gray-200 bg-gray-50">
                <Button
                    onClick={handlePurchase}
                    disabled={isProcessing || totalPrice === 0}
                    className="w-full py-6 text-lg font-bold shadow-md transition-all hover:shadow-lg bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800"
                >
                    {isProcessing ? (
                        <>
                            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                            処理中...
                        </>
                    ) : (
                        <>
                            <CreditCard className="mr-2 h-5 w-5" />
                            注文手続きへ
                        </>
                    )}
                </Button>
            </div>
        </div>
    );
};
